import { useState, useEffect, useRef } from 'react'
import { Bot, Clock, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AnimatedText } from './AnimatedText'
import { ThinkingIndicator } from './ThinkingIndicator'
import { CompactNodeProgress } from './CompactNodeProgress'
import { ChatBlockRenderer } from './ChatBlockRenderer'
import type { ChatMessage } from '@/types/chat'
import type { UIBlock } from '@/types/blocks'
import type { NodeExecution } from '@/types/orchestrator'

/** Minimum time (ms) the ThinkingIndicator stays visible */
const MIN_THINKING_MS = 600

interface AssistantMessageBubbleProps {
  message: ChatMessage
  isLive: boolean
  liveState?: {
    title: string | null
    summary: string | null
    blocks: UIBlock[]
    nodeExecutions: NodeExecution[]
    status: string
    error: string | null
    durationMs: number | null
  }
}

/**
 * Assistant message bubble — left-aligned with avatar, blocks rendered inline.
 *
 * Streaming UX flow:
 *  1. ThinkingIndicator (shimmer skeleton) shows for at least MIN_THINKING_MS
 *  2. AnimatedText reveals summary character-by-character with blinking cursor
 *  3. Blocks fade in with staggered animation
 *  4. Completion metadata fades in when done
 */
export function AssistantMessageBubble({ message, isLive, liveState }: AssistantMessageBubbleProps) {
  // Pick data source: live streaming state or finalized message
  const title = isLive ? liveState?.title : message.title
  const summary = isLive ? liveState?.summary : message.summary
  const blocks = isLive ? (liveState?.blocks ?? []) : message.blocks
  const nodeExecutions = isLive ? (liveState?.nodeExecutions ?? []) : message.nodeExecutions
  const status = isLive ? liveState?.status : message.status
  const error = isLive ? liveState?.error : message.error
  const durationMs = isLive ? liveState?.durationMs : message.durationMs

  const isStreaming = status === 'streaming'
  const isPaused = status === 'paused'
  const hasContent = !!(title || summary || blocks.length > 0)

  // ============================================================================
  // Minimum thinking time — ensures the shimmer shows for at least MIN_THINKING_MS
  // even if the backend responds instantly (common on localhost).
  // ============================================================================
  const [forceThinking, setForceThinking] = useState(false)
  const thinkingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wasLiveRef = useRef(false)

  useEffect(() => {
    // When this bubble first becomes live, start the minimum thinking timer
    if (isLive && !wasLiveRef.current) {
      wasLiveRef.current = true
      setForceThinking(true)
      thinkingTimerRef.current = setTimeout(() => {
        setForceThinking(false)
      }, MIN_THINKING_MS)
    }

    // When no longer live, clean up
    if (!isLive && wasLiveRef.current) {
      wasLiveRef.current = false
      setForceThinking(false)
      if (thinkingTimerRef.current) {
        clearTimeout(thinkingTimerRef.current)
        thinkingTimerRef.current = null
      }
    }

    return () => {
      if (thinkingTimerRef.current) {
        clearTimeout(thinkingTimerRef.current)
      }
    }
  }, [isLive])

  // Show thinking if:
  // - forced by minimum timer (always shows for first 600ms), OR
  // - naturally waiting for content (streaming but no content yet)
  const showThinking = isLive && (forceThinking || ((isStreaming || isPaused) && !hasContent))

  // Content is hidden while forceThinking is on (so shimmer → content is a clean transition)
  const showContent = !forceThinking

  return (
    <div className="flex gap-3 animate-fade-in-up">
      {/* Assistant avatar — subtle pulse while streaming */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5 transition-colors duration-300',
          isLive && (isStreaming || forceThinking)
            ? 'bg-primary/20 avatar-pulse'
            : 'bg-primary/10'
        )}
      >
        <Bot className="h-4 w-4 text-primary" />
      </div>

      {/* Message content */}
      <div className="flex-1 min-w-0 space-y-3">
        {/* Phase 1: Thinking indicator */}
        {showThinking && <ThinkingIndicator />}

        {/* Phase 2+: Actual content (hidden until minimum thinking time elapses) */}
        {showContent && (
          <>
            {/* Title */}
            {title && (
              <h3
                className={cn(
                  'font-semibold text-foreground text-[15px]',
                  isLive ? 'animate-fade-in-up' : ''
                )}
              >
                {title}
              </h3>
            )}

            {/* Summary with token animation */}
            {summary && (
              <div className={isLive ? 'animate-fade-in-up' : ''}>
                <AnimatedText
                  text={summary}
                  animate={isLive && isStreaming}
                  speed={60}
                  className="text-sm leading-relaxed"
                />
              </div>
            )}

            {/* Workflow progress (compact inline) */}
            {nodeExecutions.length > 0 && (
              <div className={isLive ? 'animate-fade-in-up' : ''}>
                <CompactNodeProgress nodes={nodeExecutions} />
              </div>
            )}

            {/* Blocks rendered inline with staggered fade-in */}
            {blocks.map((block, index) => (
              <div
                key={`${message.id}-block-${index}`}
                className={isLive ? 'animate-fade-in-up' : ''}
                style={isLive ? { animationDelay: `${index * 80}ms` } : undefined}
              >
                <ChatBlockRenderer block={block} />
              </div>
            ))}

            {/* Error display */}
            {error && (
              <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2.5 mt-1 animate-fade-in-up">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Completion metadata */}
            {status === 'completed' && durationMs != null && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground animate-fade-in-up">
                <Clock className="h-3 w-3" />
                <span>{(durationMs / 1000).toFixed(1)}s</span>
              </div>
            )}

            {/* Partial completion indicator */}
            {status === 'partial' && (
              <div className="flex items-center gap-1.5 text-xs text-warning animate-fade-in-up">
                <AlertCircle className="h-3 w-3" />
                <span>Partial response — stream interrupted</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
